import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, switchMap, catchError, filter } from 'rxjs/operators';

import { GeolocationService } from 'src/app/services/geolocation/geolocation.service';
import { PostcodeStatus, Postcode } from 'src/app/interfaces/postcode';
import { PostcodeSuggestionService } from './postcode-suggestion.service';

@Component({
  selector: 'app-postcode-field',
  templateUrl: './postcode-field.component.html',
  providers: [PostcodeSuggestionService],
  styleUrls: ['./postcode-field.component.scss']
})
export class PostcodeFieldComponent implements OnInit {
  @Input() loading = false;
  @Input() postcode: Postcode = {
    name: null,
    lat: null,
    lng: null,
    status: PostcodeStatus.Invalid,
  };
  @ViewChild('postcodeInput') postcodeInput: ElementRef;

  constructor(
    private postcodeSuggestion: PostcodeSuggestionService,
    private geolocationService: GeolocationService,
    ) {}

  /**
   *
   * @description Used by Bootstrap Typeahead to retrieve a list of matched postcodes
   * from the google maps API
   */
  getPostcodes = (text: Observable<string>) =>
    text.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      tap(() => this.loading = true),
      switchMap(term => this.postcodeSuggestion.getSuggestions(term)
        .pipe(
          catchError(() => {
            this.setInvalid();
            return of([]);
          }))
      ),
      tap(() => this.loading = false)
    )

  /**
   *
   * @description On Bootstrap Typeahead's "itemSelect" event, sets the postcode
   * object values and geolocates the postcode name
   */
  selectPostcode($event): void {
    $event.preventDefault();
    this.postcode.name = $event.item;
    this.postcode.status = PostcodeStatus.Valid;
    this.postcodeInput.nativeElement.blur();
    this.geolocatePostcode($event.item);
  }

  /**
   *
   * @description Retrieves geolocation data from geolocation service and
   * sets the postcode object values to be returned to the parent component
   */
  geolocatePostcode(postcodeName: string): void {
    if (this.postcode.name.length < 2) {
      this.setInvalid();
      return;
    }

    this.geolocationService.geolocatePostcode(postcodeName)
        .subscribe(data => {
          data.results = data.results.filter(result => result.types.indexOf('postal_code') > -1);
          if (data.results.length) {
            const coords = data.results[0].geometry.location;
            this.postcode.name = postcodeName;
            this.postcode.lat = coords.lat;
            this.postcode.lng = coords.lng;
            this.postcode.status = PostcodeStatus.Valid;
          } else {
            this.setInvalid();
          }
        });
  }

  /**
   *
   * @description Sets the postcode object status to "invalid", used
   * when a matching postcode cannot be found by Google Maps API
   */
  setInvalid(): void {
    this.postcode.lat = null;
    this.postcode.lng = null;
    this.postcode.status = PostcodeStatus.Invalid;
  }

  ngOnInit(): void {}
}
